import { OverlayModule } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'fast-deep-equal';
import { filter, map } from 'rxjs';

import {
  ComplexFilterComponent,
  ComplexFiltersModule,
  LogicalQueryFormGroupValue,
} from '@blaze-runtime/engine-ui-feature';
import {
  ComparisonOperator,
  DatasetFilterInfo,
  DatasetFilterScope,
  type LogicalQueryNode,
  LogicalType,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { findNode } from '@blaze-shared/datasets-domain';
import { ClickableDirective } from '@blaze-shared/shared-ui';
import { resizeRxUtils } from '@blaze-shared/shared-util';

import { SavedFiltersComponent } from '../saved-filters/saved-filters.component';

const LAYOUT_ROW_COLUMN_THRESHOLD_PX = 600;

@UntilDestroy()
@Component({
  selector: 'blaze-filter-builder',
  templateUrl: './filter-builder.component.html',
  styleUrls: ['./filter-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ClickableDirective,
    ComplexFilterComponent,
    ComplexFiltersModule,
    OverlayModule,
    SavedFiltersComponent,
  ],
})
export class FilterBuilderComponent implements OnChanges, AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() allowToCreatePublicFilters = false;
  @Input() filters: DatasetFilterInfo[] = [];
  @Input() isInOrganizeMode = false;
  @Input() query?: LogicalQueryNode = this.getDefaultEmptyQuery();
  @Input() showFilters = false;
  @Input() showLoadingForSavedFilters = false;
  @Input() showSavedFiltersButton = false;

  @Output() applyFilter = new EventEmitter<LogicalQueryFormGroupValue>();
  @Output() deleteFilter = new EventEmitter<DatasetFilterInfo>();
  @Output() saveFilter = new EventEmitter<{
    name: string;
    scope: DatasetFilterScope;
    query: LogicalQueryNode;
  }>();

  @ViewChild('complexFilterComponent')
  complexFilterComponent: ComplexFilterComponent | null = null;

  private appliedQuery: LogicalQueryFormGroupValue | null = null;
  private formGroupValue: LogicalQueryFormGroupValue | null = null;

  hasValidNodes = false;
  isApplyDisabled = true;
  isSavedFiltersDisabled = true;
  layout: 'row' | 'column' = 'row';
  savedFiltersDialogIsOpen = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['filters']) {
      return;
    }

    if (this.filters.length > 0) {
      this.isSavedFiltersDisabled = false;
      return;
    }

    if (this.formGroupValue) {
      this.isSavedFiltersDisabled = !this.hasValidNodes;
    }
  }

  ngAfterViewInit(): void {
    this.updateLayoutOnResize();
  }

  onQueryChange({ query }: { query: LogicalQueryFormGroupValue }): void {
    this.formGroupValue = query;
    this.hasValidNodes = this.isAtLeastOneNodeValid(this.formGroupValue);
    this.isSavedFiltersDisabled = !this.hasValidNodes && this.filters.length === 0;
    this.isApplyDisabled = !this.hasValidNodes || isEqual(this.formGroupValue, this.appliedQuery);

    this.cdr.markForCheck();
  }

  apply(): void {
    if (!this.formGroupValue) {
      return;
    }

    this.appliedQuery = this.formGroupValue;
    this.applyFilter.emit(this.formGroupValue);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
    this.cdr.markForCheck();
  }

  toggleSavedFiltersDialog(): void {
    this.savedFiltersDialogIsOpen = !this.savedFiltersDialogIsOpen;
    this.cdr.markForCheck();
  }

  applySavedFilter(filter: DatasetFilterInfo): void {
    if (this.complexFilterComponent) {
      this.query = filter.query;
      this.cdr.detectChanges();
      this.complexFilterComponent.rebuildForm();
    }

    this.onQueryChange({ query: { ...filter.query, selected: false } });
    this.apply();
    this.savedFiltersDialogIsOpen = false;
  }

  save(filter: { name: string; scope: DatasetFilterScope }): void {
    if (!this.formGroupValue) {
      return;
    }

    this.saveFilter.emit({
      name: filter.name,
      scope: filter.scope,
      query: this.formGroupValue,
    });
  }

  resetForm(): void {
    this.complexFilterComponent?.resetForm();
  }

  private getDefaultEmptyQuery(): LogicalQueryNode {
    return {
      nodeType: QueryNodeType.Logical,
      children: [
        {
          nodeType: QueryNodeType.Comparison,
          column: '',
          conditionId: '',
          value: '',
          operator: ComparisonOperator.Eq,
          isOptional: false,
          displayInfos: [],
          isFormulaValue: false,
        },
      ],
      logicalType: LogicalType.And,
    };
  }

  private updateLayoutOnResize(): void {
    resizeRxUtils
      .fromResizeObserver(this.elementRef.nativeElement)
      .pipe(
        map((resizeEntries) => resizeEntries[resizeEntries.length - 1]),
        filter((resizeData) => resizeData && resizeData.contentRect.width > 0),
        untilDestroyed(this),
      )
      .subscribe((resizeData) => {
        this.layout =
          resizeData?.contentRect.width < LAYOUT_ROW_COLUMN_THRESHOLD_PX ? 'column' : 'row';

        this.cdr.markForCheck();
      });
  }

  private isAtLeastOneNodeValid(root: LogicalQueryFormGroupValue): boolean {
    return !!findNode(
      root,
      (node) =>
        node.nodeType === QueryNodeType.Comparison &&
        node.column?.length > 0 &&
        node.operator?.length > 0,
    );
  }
}
